import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import {
  UploadSimpleIcon, SpinnerIcon, FileIcon, FilePdfIcon, FileXlsIcon, FileDocIcon, FileImageIcon, TrashIcon,
} from "@phosphor-icons/react";

/**
 * <FileUploader /> — Subida de documentos genérica (PDF, Excel, imágenes, etc.) a R2.
 * Diseñada para Document Center, comprobantes de pago, evidencia de gastos, etc.
 *
 * Props:
 *  - folder: string (default "documents")
 *  - kind: "image" | "document" | "any" (default "any")
 *  - files: array existente con {key, url, original_filename, content_type, size, id}
 *  - onUploaded: (file) => void — se llama por cada archivo subido
 *  - onRemove: (file) => void — para eliminar un archivo de la lista (no borra de R2, eso lo decide el padre)
 *  - multiple: boolean (default true)
 *  - label: string (default "Adjuntar archivo")
 */
export default function FileUploader({
  folder = "documents",
  kind = "any",
  files = [],
  onUploaded,
  onRemove,
  multiple = true,
  label = "Adjuntar archivo",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    let okCount = 0;
    for (const file of list) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" excede los 10 MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", folder);
        fd.append("kind", kind);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        onUploaded?.(data);
        okCount++;
      } catch (err) {
        toast.error(`Error subiendo "${file.name}": ${apiErr(err)}`);
      }
    }
    if (okCount > 0) toast.success(`${okCount} archivo(s) subido(s)`);
    setUploading(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md hover:border-agro-green/40 hover:bg-white/5 disabled:opacity-50"
          data-testid="file-uploader-btn"
        >
          {uploading ? (
            <>
              <SpinnerIcon size={14} className="animate-spin" /> Subiendo {progress > 0 ? `${progress}%` : "…"}
            </>
          ) : (
            <>
              <UploadSimpleIcon size={14} weight="bold" /> {label}
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple={multiple}
          onChange={handleFiles}
          className="hidden"
          accept={kind === "image" ? "image/*" : undefined}
          data-testid="file-uploader-input"
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-1.5" data-testid="file-uploader-list">
          {files.map((f) => (
            <FileRow key={f.id || f.key} file={f} onRemove={onRemove} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FileRow({ file, onRemove }) {
  const mime = file.content_type || "";
  const Icon = mime.startsWith("image/")
    ? FileImageIcon
    : mime === "application/pdf"
    ? FilePdfIcon
    : mime.includes("excel") || mime.includes("spreadsheet")
    ? FileXlsIcon
    : mime.includes("word") || mime.includes("document")
    ? FileDocIcon
    : FileIcon;

  const sizeKB = file.size ? (file.size / 1024).toFixed(0) + " KB" : "";

  return (
    <li className="flex items-center gap-3 px-3 py-2 bg-app/60 border border-border/60 rounded-md text-sm" data-testid={`file-row-${file.id || file.key}`}>
      <Icon size={20} className="text-agro-green flex-shrink-0" />
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 truncate hover:text-agro-green hover:underline"
        title={file.original_filename}
      >
        {file.original_filename || file.key}
      </a>
      <span className="text-xs text-text-muted whitespace-nowrap">{sizeKB}</span>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(file)}
          className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded transition"
          title="Eliminar"
          data-testid={`file-remove-${file.id || file.key}`}
        >
          <TrashIcon size={14} />
        </button>
      )}
    </li>
  );
}
